import React, { useState, useEffect, useCallback } from 'react'
import { postRequest } from 'src/lib/core/api-client'
import { Upload, Download, Trash2, Check, AlertTriangle } from 'react-feather'
import { X, Paperclip, Plus } from '@phosphor-icons/react'
import Alerts from '../feedback-and-dialog/Alerts'
import LoadingButton from '@mui/lab/LoadingButton'
import { apiURI } from 'src/lib/utils/constants'
import { Typography } from '@mui/material'
import { downloadFileByUrl, truncateString, classNames } from 'src/lib/utils/helpers'
import TextFieldError from './TextFieldError'
import { ErrorType, useCSVValidator } from 'src/lib/hooks/useCSVValidator'
import { parse } from 'papaparse'
import { FileDataType } from 'src/lib/types/random'
import { formatFileSize } from 'src/lib/utils/formatters'
import { InputFile } from '@shopflo/ui'

interface Props {
  setFileUrlData?: any
  setFileUrl?: any
  buttonText: string
  placeholderText?: React.ReactNode
  type: string | string[]
  uploadLink?: string
  size: 'large' | 'medium' | 'small'
  variant?: 'contained' | 'outlined' | 'text' | 'dashed'
  color?: 'inherit' | 'primary' | 'secondary' | 'success' | 'error' | 'info' | 'warning'
  acceptedSize: number
  disabled?: boolean
  baseEndpoint?: string
  uploadOnFileSave?: boolean
  setSelectedCSVFormData?: (value: any, length?: number) => void //Will return either uploaded form data or the keyword DELTED if the previously uploaded file is deleted
  infoText?: string
  prevUploadedFileName?: string //Can be used in edit scenarios to prefill previously added values
  prevUploadedFileURL?: string //Can be used in edit scenarios to prefill previously added values
  prevUploadedFileSize?: number //Can be used in edit scenarios to prefill previously added values
  setSelectedCSVPath?: React.Dispatch<any>
  errorMessage?: string
  errorStatus?: boolean
  showDeleteButton?: boolean
  errorsToCheck?: ErrorType[]
  template?: Record<string, string>
  onError?: (error: string) => void
  currentFile?: FileDataType
  deleteCurrentFile?: () => void
  customizeUI?: {
    isBorderHidden?: boolean
    isPrimaryColor?: boolean
    paddingRequired?: boolean
  }
  setUploadedFileData?: (file: FileDataType & { size?: number }) => void
}

const FileUploaderWithActions: React.FC<Props> = ({
  setFileUrlData,
  setFileUrl,
  buttonText,
  type,
  uploadLink,
  size,
  variant,
  color,
  acceptedSize,
  disabled,
  baseEndpoint = apiURI.FLO_CHECKOUT,
  uploadOnFileSave = true,
  setSelectedCSVFormData,
  placeholderText = 'Upload a CSV',
  infoText,
  prevUploadedFileName,
  prevUploadedFileURL,
  prevUploadedFileSize,
  setSelectedCSVPath,
  errorStatus,
  errorMessage,
  showDeleteButton = true,
  errorsToCheck = [],
  template,
  onError,
  currentFile,
  deleteCurrentFile,
  customizeUI = {
    isBorderHidden: false,
    isPrimaryColor: false,
    paddingRequired: true
  },
  setUploadedFileData
}) => {
  const [isUploading, setIsUploading] = useState<boolean>(false)
  const [openAlert, setOpenAlert] = useState<string>('')
  const [uploadedFile, setUploadedFile] = useState<any>()
  const [uploadedFileName, setUploadedFileName] = useState(prevUploadedFileName)
  const [fileSize, setFileSize] = useState<number>(prevUploadedFileSize ?? 0)

  const {
    loadFile,
    errors,
    resetErrors: resetCSVValidatorErrors,
    isValidating
  } = useCSVValidator({
    errorsToCheck,
    template
  })

  useEffect(() => {
    setUploadedFileName(prevUploadedFileName)
  }, [prevUploadedFileName])

  const sizeCheck = (file: any) => {
    if (file?.size > acceptedSize) {
      setOpenAlert('size')
      setIsUploading(false)
      return false
    }
    return true
  }

  const typeCheck = (file: any) => {
    const acceptedTypes = Array.isArray(type) ? type : [type]
    const fileType = file.type.toLowerCase()
    const fileExtension = file.name.split('.').pop()?.toLowerCase()

    if (!acceptedTypes.some(acceptedType => acceptedType.includes(fileType) || acceptedType.includes(fileExtension))) {
      setOpenAlert('type')
      setIsUploading(false)
      return false
    }
    return true
  }

  async function fileUpload(e: any) {
    if (!e.target?.files?.[0]) return
    setIsUploading(true)

    const file = e.target.files?.[0]
    if (!sizeCheck(file) || !typeCheck(file)) {
      setIsUploading(false)
      return
    }
    setFileSize(file?.size)
    const formData = new FormData()
    formData.append('file', file)

    setUploadedFile(file)
    setUploadedFileName(file?.name)

    try {
      const validationErrors = await loadFile(file)
      handleErrors(validationErrors)
      if (validationErrors.length > 0) {
        setIsUploading(false)
        return
      }

      if (uploadOnFileSave) {
        postRequest(uploadLink ?? '', baseEndpoint, formData)
          .then((data: any) => {
            setIsUploading(false)
            if (Boolean(setFileUrl)) {
              setFileUrl(data?.data?.file_url)
            }
            if (Boolean(setFileUrlData)) {
              parse(file, {
                header: true,
                complete: results => {
                  typeof setFileUrlData !== 'undefined' &&
                    setFileUrlData(
                      data?.data?.file_url,
                      file?.name,
                      formData,
                      results?.data?.map((row: any) => Object.values(row)[0] as string).filter(Boolean)?.length,
                      file?.size
                    )
                }
              })
            }
            setOpenAlert('success')
            setUploadedFileData &&
              setUploadedFileData({
                url: data?.data?.file_url,
                name: file?.name,
                size: file?.size
              })
          })
          .catch(error => {
            console.error(error)
            setOpenAlert('error')
            setIsUploading(false)
            handleErrors(['An unexpected error occurred while uploading the file'])
          })
      } else {
        setIsUploading(false)
        if (setSelectedCSVPath) setSelectedCSVPath(file?.name)
        parse(file, {
          header: true,
          complete: results => {
            typeof setSelectedCSVFormData !== 'undefined' &&
              setSelectedCSVFormData(
                formData,
                results?.data?.map((row: any) => Object.values(row)[0] as string).filter(Boolean)?.length
              )
          }
        })
      }
    } catch (error) {
      setIsUploading(false)
      handleErrors(['An unexpected error occurred while uploading the file'])
    }
  }

  // ** Alert handler ** //
  const handleCloseAlert = (event: any, reason: any) => {
    if (reason === 'clickaway') {
      return
    }
    setOpenAlert('')
  }

  const downloadFile = (e: React.MouseEvent) => {
    e.preventDefault()
    if (currentFile) {
      downloadFileByUrl(currentFile?.name, currentFile?.url)
      return
    }
    if (!uploadedFile) {
      if (prevUploadedFileName && prevUploadedFileURL) downloadFileByUrl(prevUploadedFileName, prevUploadedFileURL)
      return
    }
    try {
      const file = uploadedFile
      const fr = new FileReader()
      fr.readAsDataURL(file)

      const blob = new Blob([file], { type: file.type })

      const objectURL = window.URL.createObjectURL(blob)

      if (navigator?.appVersion?.toString()?.indexOf('.NET') > 0) {
        ;(window as any).navigator?.msSaveOrOpenBlob(blob, file.name)
        return
      }

      downloadFileByUrl(file.name, objectURL)
    } catch (e) {
      console.error(e)
    }
  }

  const deleteUploadedFile = () => {
    resetErrors()
    setUploadedFile(null)
    setUploadedFileName('')
    if (deleteCurrentFile) {
      deleteCurrentFile()
      return
    }
    setSelectedCSVFormData && setSelectedCSVFormData('DELETED', 0)
  }

  const handleErrors = useCallback(
    (newErrors: string[]) => {
      if (onError && newErrors.length > 0) {
        onError(newErrors[0])
      } else if (onError) {
        onError('')
      }
    },
    [onError]
  )

  const resetErrors = useCallback(() => {
    resetCSVValidatorErrors()
    handleErrors([])
  }, [resetCSVValidatorErrors, handleErrors])

  const renderUploadedState = () => {
    const isProcessing = isValidating || isUploading
    const hasError = errors.length > 0 || errorStatus

    if (variant === 'dashed') {
      return (
        <InputFile
          dimension='md'
          loading={isProcessing}
          fileName={uploadedFileName || currentFile?.name}
          onInputChange={fileUpload}
          onRemove={deleteUploadedFile}
          onDownload={() => {
            const syntheticEvent = { preventDefault: () => {} } as React.MouseEvent
            downloadFile(syntheticEvent)
          }}
          label={buttonText}
          error={hasError}
          accept={Array.isArray(type) ? type.join(',') : type}
          disabled={disabled || isUploading}
        />
      )
    }

    let statusIcon = <Check size={18} className='text-green-500' />
    if (isProcessing) {
      statusIcon = <div className='animate-spin rounded-full h-6 w-6 border-3 border-gray-200 border-t-blue-500' />
    } else if (hasError) {
      statusIcon = <AlertTriangle size={18} className='text-red-500' />
    }

    return (
      <div className='flex flex-row items-center justify-between gap-2 grow'>
        <div
          className={classNames(
            'flex flex-row items-center justify-between px-3 py-[9px] border-1 rounded-lg border-coal-lighter text-sm text-coal-dark gap-2 grow bg-white',
            hasError ? 'border-red-500' : ''
          )}
        >
          <p className='w-11/12 truncate'>
            {truncateString(uploadedFileName || currentFile?.name || '', 32)}
            {fileSize ? <span className='pl-4 text-xs text-coal-light'>{formatFileSize(fileSize)}</span> : <></>}
          </p>
          {statusIcon}
        </div>
        {!hasError && (
          <button
            className='p-2 border-1 rounded-lg border-coal-lighter text-coal-dark bg-white disabled:opacity-50'
            onClick={e => downloadFile(e)}
            disabled={isUploading}
          >
            <Download size={19} />
          </button>
        )}
        {showDeleteButton && (
          <button
            className='p-2 border-1 rounded-lg border-coal-lighter text-coal-dark bg-white disabled:opacity-50'
            onClick={deleteUploadedFile}
            disabled={isUploading}
          >
            <Trash2 size={19} className='text-coal-dark' />
          </button>
        )}
      </div>
    )
  }

  const renderEmptyState = () => {
    const hasError = errors.length > 0 || errorStatus

    if (variant === 'dashed') {
      return (
        <InputFile
          dimension='md'
          loading={isUploading || isValidating}
          fileName={undefined}
          onInputChange={fileUpload}
          onRemove={deleteUploadedFile}
          label={buttonText}
          error={hasError}
          accept={Array.isArray(type) ? type.join(',') : type}
          disabled={disabled || isUploading}
        />
      )
    }

    return (
      <div
        className={classNames(
          'flex flex-row items-center justify-between w-full py-2 rounded-lg text-sm text-coal-dark gap-2 bg-white',
          customizeUI?.isBorderHidden ? 'border-0' : 'border-1 border-coal-lighter',
          customizeUI?.paddingRequired ? 'px-3' : ''
        )}
      >
        <p className='text-sm text-coal-dark'>{placeholderText}</p>
        <LoadingButton
          component='label'
          color={color}
          loadingPosition='end'
          loading={isUploading}
          disabled={disabled}
          variant={variant}
          size={size}
          startIcon={<Upload className='scale-75' />}
          sx={{
            whiteSpace: 'nowrap',
            padding: '0.25rem 0.75rem',
            backgroundColor: customizeUI?.isPrimaryColor ? '#01CCA7' : '',
            color: customizeUI?.isPrimaryColor ? '#FFFFFF' : '#4D4D4D ',
            '&:disabled': {
              backgroundColor: '#E0E0E0',
              color: '#8D8D8D'
            },
            '&:hover': {
              backgroundColor: customizeUI?.isPrimaryColor ? '#019C85' : ''
            }
          }}
        >
          {buttonText}
          <input
            id='inputOptions'
            type='file'
            name='sourcewise'
            accept={Array.isArray(type) ? type.join(',') : type}
            value={uploadedFile} // This might need to be empty or handled differently since it's a file input
            style={{ display: 'none' }}
            onChange={fileUpload}
          />
        </LoadingButton>
      </div>
    )
  }

  return (
    <div className='w-full'>
      {uploadedFileName || currentFile ? renderUploadedState() : renderEmptyState()}

      {Boolean(infoText?.length) && !(uploadedFileName || currentFile) && (
        <Typography variant='body2' sx={{ fontSize: '12px', marginTop: '4px' }}>
          {infoText}
        </Typography>
      )}

      {errorStatus && <TextFieldError message={errorMessage ?? ''} />}
      {!errorStatus && errors.length > 0 && <TextFieldError message={errors[0]} />}

      {/* Alerts */}
      {openAlert.length ? (
        <Alerts
          open={true}
          duration={2000}
          closeHandler={handleCloseAlert}
          severity={openAlert === 'success' ? 'success' : 'error'}
          message={
            openAlert === 'success'
              ? 'Upload Successful!'
              : openAlert === 'size'
              ? `File is larger than ${acceptedSize / (1024 * 1024)} MB`
              : openAlert === 'type'
              ? `Invalid file type. Only ${Array.isArray(type) ? type.join(', ') : type} files are allowed.`
              : 'Something went wrong!'
          }
        />
      ) : null}
    </div>
  )
}

export default FileUploaderWithActions
